/**
 * Catalogue sync: full on first use, delta afterwards, refreshed quietly in the background.
 *
 * The background refresh polls `/catalogue/sync/version` — two numbers — and only pulls a delta
 * when the watermark has moved. On a shop's mobile data that is the difference between a daily
 * megabyte and a daily 200 bytes.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useLazySyncCatalogueQuery, type ShopId } from '@pos/shared';

import { commitSync, getCatalogueSnapshot, isForShop, loadCatalogue } from './store.js';

/** How often to ask whether anything changed. Long, because prices change daily, not hourly. */
const POLL_MS = 10 * 60 * 1000;

export type SyncState = {
  readonly isSyncing: boolean;
  readonly error: string | null;
  readonly lastSyncedAt: string | null;
};

export function useCatalogueSync(shopId: ShopId | null): SyncState & { sync: () => Promise<void> } {
  const [trigger] = useLazySyncCatalogueQuery();
  const [isSyncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards a second sync starting while the first is still writing to IndexedDB. A ref rather
  // than state because the check must be synchronous — two syncs racing would each read the
  // watermark before the other wrote it.
  const running = useRef(false);

  const sync = useCallback(
    async (force = false) => {
      if (shopId === null || running.current) return;

      running.current = true;
      setSyncing(true);
      setError(null);

      try {
        await loadCatalogue();

        // A cache belonging to another shop is not a delta base — it is the wrong catalogue.
        const wrongShop = !isForShop(shopId) && getCatalogueSnapshot().meta !== null;
        const since = force || wrongShop ? undefined : getCatalogueSnapshot().meta?.watermark;

        const result = await trigger({ shopId, since }).unwrap();

        // The server's own clock at query time, not the max of the returned rows' updatedAt —
        // a returned variant can carry a price change that never touched its own row at all
        // (price is a different table), so its updatedAt would understate how current this
        // sync actually is and the next delta would needlessly re-fetch it forever.
        await commitSync(
          result.variants,
          result.removedVariantIds,
          {
            watermark: result.syncedAt,
            syncedAt: result.syncedAt,
            shopId,
            count: result.variants.length,
          },
          result.isFullSnapshot || wrongShop,
        );
      } catch (caught) {
        // A failed sync is not a failed app. The cached catalogue still searches; the banner
        // says how stale it is and offers a retry.
        setError(
          caught instanceof Error
            ? caught.message
            : 'The catalogue could not be updated. You can keep selling from the cached copy.',
        );
      } finally {
        running.current = false;
        setSyncing(false);
      }
    },
    [shopId, trigger],
  );

  // External system: a wall-clock interval, plus the browser's visibility and connectivity
  // events. None of this is derivable from render.
  useEffect(() => {
    if (shopId === null) return;

    void sync();

    const tick = () => {
      if (document.visibilityState === 'visible' && navigator.onLine) void sync();
    };

    const timer = window.setInterval(tick, POLL_MS);
    // A phone that has been in a pocket for an hour should refresh when it comes back out,
    // rather than waiting up to ten minutes for the next tick.
    document.addEventListener('visibilitychange', tick);
    window.addEventListener('online', tick);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
      window.removeEventListener('online', tick);
    };
  }, [shopId, sync]);

  return {
    isSyncing,
    error,
    lastSyncedAt: getCatalogueSnapshot().meta?.syncedAt ?? null,
    sync: useCallback(() => sync(true), [sync]),
  };
}
